import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partOrderService } from '../../services/api';

const CutSheetViewer = () => {
    const navigate = useNavigate();
    const [cutSheetData, setCutSheetData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);

    useEffect(() => {
        const loadCutSheet = async () => {
            try {
                setLoading(true);
                const response = await partOrderService.generateCutSheet();
                setCutSheetData(response.data);

                // Pre-select the first vendor if any exists
                if (response.data.by_vendor && Object.keys(response.data.by_vendor).length > 0) {
                    setSelectedVendor(Object.keys(response.data.by_vendor)[0]);
                }
            } catch (err) {
                console.error('Error loading cut sheet data:', err);
                setError('Failed to load cut sheet data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadCutSheet();
    }, []);

    const handleCreateOrder = (vendorId) => {
        // Navigate to the order form with the cut sheet data for the selected vendor
        const vendorData = {
            ...cutSheetData,
            by_vendor: { [vendorId]: cutSheetData.by_vendor[vendorId] }
        };
        navigate('/part-orders/new', { state: { cutSheetData: { data: vendorData } } });
    };

    const handleCreateAllOrders = async () => {
        try {
            setLoading(true);
            const result = await partOrderService.createOrdersFromCutSheet();
            alert(`${result.message} - ${result.data.length} orders created`);
            navigate('/part-orders');
        } catch (err) {
            console.error('Error creating orders:', err);
            setError('Failed to create orders. Please try again.');
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline"> {error}</span>
                </div>
            </div>
        );
    }

    if (!cutSheetData || !cutSheetData.cut_sheet || cutSheetData.cut_sheet.length === 0) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Cut Sheet</h2>
                    <div className="text-center py-8 text-gray-500">
                        No items need to be ordered at this time.
                    </div>
                    <div className="flex justify-center mt-4">
                        <button
                            onClick={() => navigate('/part-orders')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                        >
                            Return to Orders
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const vendorIds = Object.keys(cutSheetData.by_vendor);

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Cut Sheet</h1>
                <div className="flex space-x-3">
                    <button
                        onClick={handleCreateAllOrders}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none"
                    >
                        Create All Orders
                    </button>
                    <button
                        onClick={() => navigate('/part-orders')}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none"
                    >
                        Back to Orders
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Cut Sheet Summary</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Generated on: {new Date(cutSheetData.generated_at).toLocaleString()}
                    </p>
                </div>
                <div className="p-6">
                    <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-800 mb-3">Vendors</h3>
                        <div className="flex flex-wrap gap-2">
                            {vendorIds.map(vendorId => {
                                const items = cutSheetData.by_vendor[vendorId];
                                const vendorName = items[0]?.vendor_name || `Vendor ${vendorId}`;
                                const isSelected = selectedVendor === vendorId;

                                return (
                                    <button
                                        key={vendorId}
                                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                                            isSelected
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                        }`}
                                        onClick={() => setSelectedVendor(vendorId)}
                                    >
                                        {vendorName} ({items.length} items)
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {selectedVendor && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-800">
                                    {cutSheetData.by_vendor[selectedVendor][0]?.vendor_name || `Vendor ${selectedVendor}`} Items
                                </h3>
                                <button
                                    onClick={() => handleCreateOrder(selectedVendor)}
                                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 focus:outline-none"
                                >
                                    Create Order for this Vendor
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Type</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor SKU</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Threshold</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Quantity</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {cutSheetData.by_vendor[selectedVendor].map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.product_sku}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.part_type}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.vendor_sku || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.current_stock}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.required_for_orders}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.threshold}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.quantity_to_order}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CutSheetViewer;
